/**
 * Generative Art module constants.
 * 20 approved generators across 4 families, 41 curated palettes.
 *
 * Created: Session 39 plan → Session 41 implementation (Phase A)
 * Source: docs/gen-art/GEN-ART-MODULE-PLAN.md
 */

// ═══════════════════════════════════════════════════════════
//  FAMILIES
// ═══════════════════════════════════════════════════════════

export const FAMILIES = [
  { id: 'organic',  name: 'Organic',  accent: '#52b788', accentGlow: 'rgba(82,183,136,0.3)',  count: 7 },
  { id: 'flow',     name: 'Flow',     accent: '#60c8f0', accentGlow: 'rgba(96,200,240,0.3)',  count: 3 },
  { id: 'geo',      name: 'Geo',      accent: '#FF9500', accentGlow: 'rgba(255,149,0,0.3)',   count: 6  },
  { id: 'poster',   name: 'Poster',   accent: '#E63946', accentGlow: 'rgba(230,57,70,0.3)',   count: 3  },
  { id: 'strata',   name: 'Strata',   accent: '#8d6e63', accentGlow: 'rgba(141,110,99,0.3)',  count: 2  },
  { id: 'normies',  name: 'Normies',  accent: '#48494b', accentGlow: 'rgba(72,73,75,0.3)',   count: 1  },
];

// ═══════════════════════════════════════════════════════════
//  GENERATORS — 20 approved (screenshot-verified)
// ═══════════════════════════════════════════════════════════

export const GENERATORS = [
  // ── Organic family (7) ──
  { id: 'circlePack',   family: 'organic', name: 'Circle Pack',   desc: 'Pressure-packed circles with rain streaks' },
  { id: 'stipple',      family: 'organic', name: 'Stipple',       desc: 'Noise-driven pointillism' },
  { id: 'erosion',      family: 'organic', name: 'Erosion',       desc: 'Layered terrain with eroded valleys' },
  { id: 'smoke',        family: 'organic', name: 'Smoke',         desc: 'Ethereal domain-warped plumes' },
  { id: 'particles',    family: 'organic', name: 'Particles',     desc: 'Connected particle constellation' },
  { id: 'topography',   family: 'organic', name: 'Topography',    desc: 'Contour elevation map lines' },
  { id: 'domainWarp',   family: 'organic', name: 'Domain Warp',   desc: 'Multi-layer fbm organic marble' },

  // ── Flow family (3) — luminescent lines / threads ──
  { id: 'ridged',       family: 'flow', name: 'Ridged',        desc: 'Luminescent ridgeline mountain bands' },
  { id: 'silkThreads',  family: 'flow', name: 'Silk Threads',  desc: 'Thousands of delicate flowing threads' },
  { id: 'fluid',        family: 'flow', name: 'Fluid',         desc: 'Navier-Stokes inspired brush strokes' },

  // ── Geo Simple family (6) ──
  { id: 'halftonePanel', family: 'geo', name: 'Halftone',         desc: 'Ben-Day dot grid panels' },
  { id: 'blockMaze',     family: 'geo', name: 'Block Maze',       desc: 'Voronoi zone rectangular grid' },
  { id: 'truchetCurves', family: 'geo', name: 'Truchet',          desc: 'Quarter-circle tile curves' },
  { id: 'geoComposition',family: 'geo', name: 'Composition',      desc: 'Mondrian-style subdivisions' },
  { id: 'concentricFrames',family:'geo', name: 'Concentric',      desc: 'Nested rectangular frames' },
  { id: 'sunburst',      family: 'geo', name: 'Sunburst',         desc: 'Radial wedge segments' },

  // ── Poster (Parametric) family (3) ──
  { id: 'blockMosaic',  family: 'poster', name: 'Block Mosaic',   desc: 'Rotated grid with hatching patterns' },
  { id: 'checkerWarp',  family: 'poster', name: 'Checker Warp',   desc: 'Warped checkerboard with scale variation' },
  { id: 'lineField',    family: 'poster', name: 'Line Field',     desc: 'Flow-directed short line strokes' },

  // ── Strata (Simulation) family (2) ──
  { id: 'strata',       family: 'strata', name: 'Strata',         desc: 'Sand grain vertical-line simulation' },
  { id: 'meridian',     family: 'strata', name: 'Meridian',       desc: 'Particle sand landscape — geological strata layers' },

  // ── Normies family (1) — on-chain pixel art ──
  { id: 'normiePortrait', family: 'normies', name: 'Portrait', desc: 'Full-background Normie portrait from token ID' },
];

// Quick lookup
export const GENERATOR_MAP = Object.fromEntries(GENERATORS.map(g => [g.id, g]));

// ═══════════════════════════════════════════════════════════
//  PER-GENERATOR PARAMETER DEFINITIONS
// ═══════════════════════════════════════════════════════════

/** Organic family — shared controls */
const ORGANIC_PARAMS = [
  { key: 'scale',      label: 'Scale',      min: 0.10, max: 2.00, step: 0.01, default: 0.50 },
  { key: 'density',    label: 'Density',    min: 0.10, max: 1.00, step: 0.01, default: 0.50 },
  { key: 'complexity', label: 'Complexity', min: 1.0,  max: 10.0, step: 0.1,  default: 5.0  },
];

/** Geo Simple family — basic controls */
const GEO_SIMPLE_PARAMS = [
  { key: 'cellSize', label: 'Cell Size', min: 3,   max: 20,  step: 1,    default: 8   },
  { key: 'density',  label: 'Density',   min: 0.3, max: 1.0, step: 0.05, default: 0.7 },
];

/** Per-generator parameter definitions */
export const PARAM_DEFS = {
  // Organic — all share the same 3 controls
  circlePack:   ORGANIC_PARAMS,
  stipple:      ORGANIC_PARAMS,
  erosion:      ORGANIC_PARAMS,
  smoke:        ORGANIC_PARAMS,
  particles:    ORGANIC_PARAMS,
  topography:   ORGANIC_PARAMS,
  domainWarp:   ORGANIC_PARAMS,
  ridged:       ORGANIC_PARAMS,
  silkThreads:  ORGANIC_PARAMS,
  fluid:        ORGANIC_PARAMS,

  // Geo Simple — shared basic controls
  halftonePanel:    GEO_SIMPLE_PARAMS,
  blockMaze:        GEO_SIMPLE_PARAMS,
  truchetCurves:    GEO_SIMPLE_PARAMS,
  geoComposition:   GEO_SIMPLE_PARAMS,
  concentricFrames: GEO_SIMPLE_PARAMS,
  sunburst:         GEO_SIMPLE_PARAMS,

  // Poster — per-generator unique controls
  blockMosaic: [
    { key: 'cols',         label: 'Grid Size',      min: 4,   max: 20,  step: 1,    default: 10  },
    { key: 'fillProb',     label: 'Fill %',         min: 0.1, max: 0.9, step: 0.05, default: 0.5 },
    { key: 'angle',        label: 'Rotation',       min: -15, max: 15,  step: 1,    default: 5   },
    { key: 'spanProb',     label: 'Span %',         min: 0.0, max: 0.5, step: 0.05, default: 0.3 },
    { key: 'patternProb',  label: 'Inner Pattern',  min: 0.0, max: 1.0, step: 0.1,  default: 0.5 },
    { key: 'darkBg',       label: 'Dark BG',        min: 0,   max: 1,   step: 1,    default: 0   },
    { key: 'borderW',      label: 'Border',         min: 0,   max: 30,  step: 2,    default: 14  },
  ],
  checkerWarp: [
    { key: 'size',      label: 'Cell Size',   min: 15,  max: 100, step: 5,    default: 50   },
    { key: 'warpX',     label: 'Warp X',      min: 0.0, max: 0.6, step: 0.05, default: 0.15 },
    { key: 'warpY',     label: 'Warp Y',      min: 0.0, max: 0.6, step: 0.05, default: 0.15 },
    { key: 'scaleVar',  label: 'Scale Var',   min: 0.0, max: 1.0, step: 0.05, default: 0.3  },
    { key: 'fillAll',   label: 'Fill All',    min: 0.0, max: 1.0, step: 0.1,  default: 0.3  },
  ],
  lineField: [
    { key: 'numLines',   label: 'Lines',      min: 100,   max: 2000, step: 50,    default: 800   },
    { key: 'lineLen',    label: 'Length',      min: 3,     max: 60,   step: 1,     default: 18    },
    { key: 'lineW',      label: 'Width',       min: 0.5,   max: 6.0,  step: 0.25,  default: 2.0   },
    { key: 'flowScale',  label: 'Flow Scale',  min: 0.001, max: 0.015,step: 0.001, default: 0.005 },
    { key: 'multiColor', label: 'Multi Color', min: 0,     max: 1,    step: 1,     default: 0     },
    { key: 'darkBg',     label: 'Dark BG',     min: 0,     max: 1,    step: 1,     default: 1     },
  ],

  // Strata — grain simulation controls
  strata: [
    { key: 'nozzleWidth',   label: 'Nozzle',     min: 1,   max: 10,   step: 1,   default: 4    },
    { key: 'layers',        label: 'Layers',      min: 6,   max: 30,   step: 1,   default: 14   },
    { key: 'windStrength',  label: 'Wind',        min: 0,   max: 100,  step: 5,   default: 30   },
    { key: 'reposeAngle',   label: 'Repose',      min: 1,   max: 8,    step: 1,   default: 3    },
  ],

  // Meridian — particle sand landscape controls
  meridian: [
    { key: 'layers',        label: 'Layers',      min: 3,   max: 20,   step: 1,   default: 7    },
    { key: 'windStrength',  label: 'Wind',        min: 0,   max: 10,   step: 0.5, default: 3    },
    { key: 'reposeAngle',   label: 'Repose',      min: 1,   max: 8,    step: 1,   default: 2    },
    { key: 'nozzleVar',     label: 'Nozzle Var',  min: 1,   max: 10,   step: 1,   default: 3    },
  ],

  // Normie Portrait — on-chain pixel art
  normiePortrait: [
    { key: 'tokenId', label: 'Normie ID', min: 1, max: 9999, step: 1, default: 42, editable: true },
  ],
};

/** Get default params for a generator */
export function getDefaultParams(generatorId) {
  const defs = PARAM_DEFS[generatorId] || [];
  const obj = {};
  defs.forEach(d => { obj[d.key] = d.default; });
  return obj;
}

// ═══════════════════════════════════════════════════════════
//  PALETTES — 48 unique palettes across 4 families (12 each)
// ═══════════════════════════════════════════════════════════

export const PALETTES = [
  // ── Organic family (12 palettes — dark/moody) ──
  { id: 'organic-midnight', name: 'Midnight', family: 'organic',
    colors: ['#1a1a3e','#2d1b69','#4a1a7a','#7b2d8e','#b84daa','#e87fbe','#f5b8d6'], bg: '#08081a', accent: '#b84daa' },
  { id: 'organic-forest', name: 'Forest', family: 'organic',
    colors: ['#0d1f12','#1a3a20','#2d5a35','#3d7a4a','#5aaa6a','#7dd49a','#a8f0c0'], bg: '#060f08', accent: '#5aaa6a' },
  { id: 'organic-ember', name: 'Ember', family: 'organic',
    colors: ['#3d1508','#6b1e0a','#a03010','#d04820','#e87040','#f0a060','#f8d0a0'], bg: '#140805', accent: '#e87040' },
  { id: 'organic-ocean', name: 'Ocean', family: 'organic',
    colors: ['#0a1830','#102a50','#1a4a80','#2070b0','#30a0e0','#60c8f0','#a0e8ff'], bg: '#040a14', accent: '#30a0e0' },
  { id: 'organic-aurora', name: 'Aurora', family: 'organic',
    colors: ['#0a2040','#103060','#1a6050','#20a060','#40d080','#80e0b0','#c0f0e0'], bg: '#040810', accent: '#40d080' },
  { id: 'organic-dusk', name: 'Dusk', family: 'organic',
    colors: ['#2a1018','#4a1828','#7a2040','#aa3060','#d05080','#e880a0','#f0b0c0'], bg: '#100608', accent: '#d05080' },
  { id: 'organic-gold', name: 'Gold', family: 'organic',
    colors: ['#2a2008','#4a3810','#7a5a18','#aa8020','#d0a830','#e8c850','#f0e080'], bg: '#0e0c04', accent: '#d0a830' },
  { id: 'organic-neon', name: 'Neon', family: 'organic',
    colors: ['#1a0040','#3a0080','#00aa80','#00ff80','#ff0080','#ff80ff','#80ffff'], bg: '#060608', accent: '#00ff80' },
  { id: 'organic-earth', name: 'Earth', family: 'organic',
    colors: ['#2a2018','#4a3828','#6a5040','#8a7058','#aa9070','#c8b090','#e0d0b0'], bg: '#0c0a06', accent: '#aa9070' },
  { id: 'organic-arctic', name: 'Arctic', family: 'organic',
    colors: ['#141828','#1e2840','#284060','#306080','#4080a0','#60a0c0','#90c8e0'], bg: '#080a10', accent: '#60a0c0' },
  { id: 'organic-plasma', name: 'Plasma', family: 'organic',
    colors: ['#0d0887','#46039f','#7201a8','#9c179e','#bd3786','#d8576b','#ed7953','#fb9f3a','#fdca26'], bg: '#06041a', accent: '#bd3786' },
  { id: 'organic-moss', name: 'Moss', family: 'organic',
    colors: ['#1a1c0a','#2a3010','#3a4418','#506020','#708030','#90a040','#b8c860'], bg: '#0a0c04', accent: '#90a040' },

  // ── Geo Simple family (12 palettes — bold/graphic) ──
  { id: 'geo-neonpop', name: 'Neon Pop', family: 'geo',
    colors: ['#ff2d6b','#ffd23f','#3a86ff','#8338ec','#ff006e'], bg: '#1a1a2e', accent: '#ff2d6b',
    border: '#ffd23f', stroke: '#1a1a2e' },
  { id: 'geo-bauhaus', name: 'Bauhaus', family: 'geo',
    colors: ['#e63946','#457b9d','#f4a261','#264653','#2a9d8f'], bg: '#f5f0e1', accent: '#e63946',
    border: '#264653', stroke: '#264653' },
  { id: 'geo-midnight', name: 'Midnight Geo', family: 'geo',
    colors: ['#00b4d8','#90e0ef','#e0aaff','#ff5400','#ffd60a'], bg: '#0d1b2a', accent: '#00b4d8',
    border: '#00b4d8', stroke: '#0d1b2a' },
  { id: 'geo-candy', name: 'Candy', family: 'geo',
    colors: ['#ff69b4','#ff85a2','#ffc8dd','#a855f7','#6366f1'], bg: '#fff0f3', accent: '#ff69b4',
    border: '#ff69b4', stroke: '#2d2d2d' },
  { id: 'geo-solar', name: 'Solar', family: 'geo',
    colors: ['#14213d','#000000','#ffffff','#e63946','#ffd60a'], bg: '#fca311', accent: '#14213d',
    border: '#14213d', stroke: '#14213d' },
  { id: 'geo-forest', name: 'Forest Geo', family: 'geo',
    colors: ['#000000','#40916c','#95d5b2','#ffd166','#d4e09b'], bg: '#2d6a4f', accent: '#40916c',
    border: '#ffd166', stroke: '#1b4332' },
  { id: 'geo-brutalist', name: 'Brutalist', family: 'geo',
    colors: ['#000000','#ff0000','#0000ff','#ffff00'], bg: '#ffffff', accent: '#ff0000',
    border: '#000000', stroke: '#000000' },
  { id: 'geo-vaporwave', name: 'Vaporwave', family: 'geo',
    colors: ['#ff8906','#e53170','#7f5af0','#2cb67d','#fffffe'], bg: '#0f0e17', accent: '#7f5af0',
    border: '#ff8906', stroke: '#0f0e17' },
  { id: 'geo-terracotta', name: 'Terracotta', family: 'geo',
    colors: ['#c1440e','#e77d11','#2d4739','#1a1a2e','#d4a373'], bg: '#faf0e6', accent: '#c1440e',
    border: '#c1440e', stroke: '#2d2d2d' },
  { id: 'geo-arctic', name: 'Arctic Geo', family: 'geo',
    colors: ['#006d77','#83c5be','#ffddd2','#264653','#2a9d8f'], bg: '#e8f1f2', accent: '#006d77',
    border: '#264653', stroke: '#264653' },
  { id: 'geo-obsidian', name: 'Obsidian', family: 'geo',
    colors: ['#4a4e69','#9a8c98','#c9ada7','#22223b','#f2e9e4'], bg: '#1a1a2e', accent: '#9a8c98',
    border: '#4a4e69', stroke: '#22223b' },
  { id: 'geo-citrus', name: 'Citrus', family: 'geo',
    colors: ['#f72585','#b5179e','#7209b7','#3a0ca3','#4cc9f0'], bg: '#f0f7da', accent: '#f72585',
    border: '#b5179e', stroke: '#3a0ca3' },

  // ── Poster (Parametric) family (12 palettes) ──
  { id: 'poster-bauhaus', name: 'Bauhaus P', family: 'poster',
    colors: ['#E63946','#F4A261','#2A9D8F','#264653','#E9C46A'], bg: '#F5F0E1', accent: '#E63946' },
  { id: 'poster-pop', name: 'Pop', family: 'poster',
    colors: ['#FF69B4','#FFD700','#7B68EE','#00BFFF','#FF6347'], bg: '#F5F0E1', accent: '#FF69B4' },
  { id: 'poster-mondrian', name: 'Mondrian', family: 'poster',
    colors: ['#D40920','#1356A2','#F7D842','#FFFFFF','#000000'], bg: '#F5F0E1', accent: '#D40920' },
  { id: 'poster-sunset', name: 'Sunset', family: 'poster',
    colors: ['#FF6B6B','#FFA07A','#FFD93D','#6BCB77','#4D96FF'], bg: '#F5F0E1', accent: '#FF6B6B' },
  { id: 'poster-neonnight', name: 'Neon Night', family: 'poster',
    colors: ['#FF00FF','#00FFFF','#FFFF00','#FF4444','#7B2FBE'], bg: '#0a0a0a', accent: '#FF00FF' },
  { id: 'poster-earth', name: 'Earth P', family: 'poster',
    colors: ['#C06014','#D4A76A','#2D5016','#8B6914','#3D1C02'], bg: '#F5F0E1', accent: '#C06014' },
  { id: 'poster-ocean', name: 'Ocean P', family: 'poster',
    colors: ['#003F5C','#2F6690','#58A4B0','#A9D6E5','#D9ED92'], bg: '#F5F0E1', accent: '#003F5C' },
  { id: 'poster-coral', name: 'Coral', family: 'poster',
    colors: ['#FF6F61','#FFB5A7','#FCD5CE','#F8EDEB','#F9DCC4'], bg: '#F5F0E1', accent: '#FF6F61' },
  { id: 'poster-bw', name: 'BW', family: 'poster',
    colors: ['#000000','#333333','#666666','#999999','#FFFFFF'], bg: '#F5F0E1', accent: '#666666' },
  { id: 'poster-retro', name: 'Retro', family: 'poster',
    colors: ['#FF5733','#FFC300','#DAF7A6','#C70039','#900C3F'], bg: '#F5F0E1', accent: '#FF5733' },
  { id: 'poster-mint', name: 'Mint', family: 'poster',
    colors: ['#2EC4B6','#CBF3F0','#FF9F1C','#FFBF69','#011627'], bg: '#F5F0E1', accent: '#2EC4B6' },
  { id: 'poster-plum', name: 'Plum', family: 'poster',
    colors: ['#7B2D8E','#D4A5FF','#FF6B8A','#2D3A4A','#F0C040'], bg: '#F5F0E1', accent: '#7B2D8E' },

  // ── Strata (Simulation) family (12 palettes — geological) ──
  { id: 'strata-emerald', name: 'Emerald', family: 'strata',
    colors: ['#1a6b3c','#2d9e5a','#45b86e','#0b3d1e','#7de8a8','#0f4428','#3cb371','#165c32','#267a48'], bg: '#f0ece4', accent: '#2d9e5a' },
  { id: 'strata-desert', name: 'Desert', family: 'strata',
    colors: ['#d4a574','#c4866a','#3366aa','#e8c4a8','#8a8a8a','#b8967a','#2255a0','#c9a088','#a07860'], bg: '#f0ece4', accent: '#d4a574' },
  { id: 'strata-spectrum', name: 'Spectrum', family: 'strata',
    colors: ['#1a6b3c','#e8a030','#d04080','#2255a0','#f0c040','#45b86e','#c86030','#304890','#e890a0'], bg: '#f0ece4', accent: '#e8a030' },
  { id: 'strata-indigo', name: 'Indigo', family: 'strata',
    colors: ['#2d2d6b','#3535a0','#e86830','#1e1e50','#4040b8','#f07030','#2a2a80','#1a1a45','#5050c8'], bg: '#f0ece4', accent: '#3535a0' },
  { id: 'strata-tropic', name: 'Tropic', family: 'strata',
    colors: ['#2aa07a','#60c8a0','#d8a030','#108868','#40b090','#c89020','#1a7060','#e8b840','#80d4b0'], bg: '#f0ece4', accent: '#60c8a0' },
  { id: 'strata-sunset', name: 'Sunset S', family: 'strata',
    colors: ['#e86040','#d04080','#f0a830','#c83060','#40a080','#e87050','#b82050','#f0c060','#d85070'], bg: '#f0ece4', accent: '#e86040' },
  { id: 'strata-ocean', name: 'Ocean S', family: 'strata',
    colors: ['#3080c0','#5098d0','#80b8e0','#e87050','#2468a8','#d06040','#6aaad0','#f08060','#1858a0'], bg: '#f0ece4', accent: '#3080c0' },
  { id: 'strata-mono', name: 'Mono', family: 'strata',
    colors: ['#111111','#2a2a2a','#4a4a4a','#707070','#999999','#c0c0c0','#e8e8e8','#b0b0b0','#3a3a3a'], bg: '#0a0a0a', accent: '#999999' },
  { id: 'strata-earth', name: 'Earth S', family: 'strata',
    colors: ['#c84040','#d8a050','#308050','#2860a0','#e0c060','#a03030','#48a070','#204880','#c06040'], bg: '#f0ece4', accent: '#d8a050' },
  { id: 'strata-rose', name: 'Rose', family: 'strata',
    colors: ['#8e3a59','#c75b7a','#e8899a','#f0b8c4','#a04068','#d0687e','#7a2848','#f0a0b0','#b85070'], bg: '#f0ece4', accent: '#c75b7a' },
  { id: 'strata-slate', name: 'Slate', family: 'strata',
    colors: ['#3a4a5c','#506478','#6a8090','#84a0b0','#2a3848','#5c7488','#c09850','#4a6070','#98b0c0'], bg: '#f0ece4', accent: '#6a8090' },
  { id: 'strata-amber', name: 'Amber', family: 'strata',
    colors: ['#b87820','#d4a030','#e8c050','#f0d870','#a06818','#c89028','#8a5810','#f0e080','#d0b040'], bg: '#f0ece4', accent: '#d4a030' },

  // ── Normies family (7 palettes — monochrome/on-chain) ──
  { id: 'normies-classic', name: 'Classic', family: 'normies',
    colors: ['#48494b','#737577','#a0a2a4','#d0d2d4','#e3e5e4','#ffffff'], bg: '#0a0a0a', accent: '#e3e5e4',
    border: '#48494b', stroke: '#0a0a0a', renderMode: 'original', genArtOnly: true },
  { id: 'normies-inverted', name: 'Inverted', family: 'normies',
    colors: ['#e3e5e4','#a0a2a4','#737577','#48494b','#1a1a1a','#000000'], bg: '#f0f0f0', accent: '#48494b',
    border: '#a0a2a4', stroke: '#f0f0f0', renderMode: 'invert', genArtOnly: true },
  { id: 'normies-mono', name: 'Mono', family: 'normies',
    colors: ['#48494b','#737577','#e3e5e4','#ffffff','#48494b'], bg: '#0a0a0a', accent: '#48494b',
    border: '#48494b', stroke: '#0a0a0a' },
  { id: 'normies-emerald', name: 'Emerald', family: 'normies',
    colors: ['#1a6b3c','#2d9e5a','#45b86e','#0b3d1e','#7de8a8','#0f4428','#3cb371','#165c32','#267a48'], bg: '#0a0a0a', accent: '#2d9e5a',
    border: '#2d9e5a', stroke: '#0a0a0a' },
  { id: 'normies-phantom', name: 'Phantom', family: 'normies',
    colors: ['#0e1b2e','#1a3050','#2a5080','#4080b0','#60b0e0','#a0d8f0'], bg: '#060d18', accent: '#4080b0',
    border: '#2a5080', stroke: '#060d18', renderMode: 'colorize', genArtOnly: true },
  { id: 'normies-gilded', name: 'Gilded', family: 'normies',
    colors: ['#3a2a0a','#6b4e12','#b8860b','#d4a030','#f0c850','#f8e898'], bg: '#0a0804', accent: '#d4a030',
    border: '#b8860b', stroke: '#0a0804', renderMode: 'colorize', genArtOnly: true },
  { id: 'normies-awakened', name: 'Awakened', family: 'normies',
    colors: ['#48494b','#e3e5e4','#6366f1','#818cf8','#48494b'], bg: '#0a0a0a', accent: '#6366f1',
    border: '#6366f1', stroke: '#0a0a0a' },
  { id: 'normies-burn', name: 'Burn', family: 'normies',
    colors: ['#48494b','#737577','#e3e5e4','#ef4444','#48494b'], bg: '#0a0a0a', accent: '#ef4444',
    border: '#ef4444', stroke: '#0a0a0a' },

  // Fiery palettes for Burn mode
  { id: 'burn-inferno', name: 'Inferno', family: 'burn',
    colors: ['#8b1a1a','#c0392b','#e74c3c','#f39c12','#f1c40f','#d35400','#ff6348','#a93226','#e55039'], bg: '#0a0a0a', accent: '#e74c3c',
    border: '#e74c3c', stroke: '#0a0a0a' },
  { id: 'burn-ember', name: 'Ember', family: 'burn',
    colors: ['#4a1a0a','#7a2e0e','#b34212','#d4601a','#e8832a','#f5a623','#ffcc02','#c75b0a','#953108'], bg: '#0a0a0a', accent: '#d4601a',
    border: '#d4601a', stroke: '#0a0a0a' },

  // GM palettes
  { id: 'gm-bauhaus', name: 'Bauhaus', family: 'gm',
    colors: ['#d92b2b','#1a56db','#f2c91a','#1a1a1a','#e8e4d9','#b8291e','#2260a6','#d9a816','#3a3a3a'], bg: '#e8e4d9', accent: '#d92b2b',
    border: '#d92b2b', stroke: '#1a1a1a' },
  { id: 'gm-arctic', name: 'Arctic Geo', family: 'gm',
    colors: ['#a8d8ea','#5bb8d4','#2e8bc0','#1a5276','#0c2d48','#d4f1f9','#76d7ea','#3a95b4','#e0f7fa'], bg: '#0c2d48', accent: '#5bb8d4',
    border: '#5bb8d4', stroke: '#0c2d48' },
  { id: 'gm-neon', name: 'Neon Pop', family: 'gm',
    colors: ['#ff2d95','#00f0ff','#b8ff00','#a855f7','#ff6b2b','#00ff88','#ffee00','#e040fb','#18ffff'], bg: '#0a0a12', accent: '#ff2d95',
    border: '#ff2d95', stroke: '#0a0a12' },
  { id: 'gm-candy', name: 'Candy', family: 'gm',
    colors: ['#f9a8d4','#c084fc','#93c5fd','#fda4af','#fdba74','#d8b4fe','#a5f3fc','#fca5a5','#f0abfc'], bg: '#1a1025', accent: '#f9a8d4',
    border: '#f9a8d4', stroke: '#1a1025' },
  { id: 'gm-obsidian', name: 'Obsidian', family: 'gm',
    colors: ['#1a1a2e','#16213e','#0f3460','#2c2c54','#3c3c6e','#474787','#5a5a8f','#1b1b3a','#252550'], bg: '#0a0a14', accent: '#474787',
    border: '#474787', stroke: '#0a0a14' },
];

// Quick lookup
export const PALETTE_MAP = Object.fromEntries(PALETTES.map(p => [p.id, p]));

/** Get palettes filtered by family (or all). Flow shares organic palettes. */
export function getPalettesByFamily(familyId) {
  if (!familyId || familyId === 'all') return PALETTES;
  // Flow family reuses organic palettes
  const lookupId = familyId === 'flow' ? 'organic' : familyId;
  return PALETTES.filter(p => p.family === lookupId);
}

/** Get the default palette for a family */
export function getDefaultPalette(familyId) {
  switch (familyId) {
    case 'organic': return 'organic-ocean';
    case 'flow':    return 'organic-ocean';
    case 'geo':     return 'geo-neonpop';
    case 'poster':  return 'poster-bauhaus';
    case 'strata':  return 'strata-emerald';
    case 'normies': return 'normies-classic';
    default:        return 'organic-ocean';
  }
}

/** Get the default generator for a family */
export function getDefaultGenerator(familyId) {
  switch (familyId) {
    case 'organic': return 'circlePack';
    case 'flow':    return 'ridged';
    case 'geo':     return 'halftonePanel';
    case 'poster':  return 'lineField';
    case 'strata':  return 'strata';
    case 'normies': return 'normiePortrait';
    default:        return 'ridged';
  }
}

// ═══════════════════════════════════════════════════════════
//  COLOR UTILITIES
// ═══════════════════════════════════════════════════════════

export function hexToRgb(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const d = mx - mn;
  const s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
  let h = 0;
  if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (mx === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hslToRgb(h, s, l) {
  h = ((h % 1) + 1) % 1;
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p, q, t) => {
    t = ((t % 1) + 1) % 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  ];
}

/**
 * Adjust a single hex color by hue shift, saturation multiplier, and brightness multiplier.
 * @param {string} hex - '#rrggbb'
 * @param {number} hueShift - degrees (0–360)
 * @param {number} satMul - multiplier (0–2, default 1)
 * @param {number} briMul - multiplier (0.5–1.5, default 1)
 * @returns {string} 'rgb(r,g,b)'
 */
export function adjustColor(hex, hueShift = 0, satMul = 1, briMul = 1) {
  const [r, g, b] = hexToRgb(hex);
  let [h, s, l] = rgbToHsl(r, g, b);
  h += hueShift / 360;
  s = Math.min(1, Math.max(0, s * satMul));
  l = Math.min(1, Math.max(0, l * briMul));
  const [r2, g2, b2] = hslToRgb(h, s, l);
  return `rgb(${r2},${g2},${b2})`;
}

/**
 * Convert an adjusted RGB tuple [r,g,b] to hex string '#rrggbb'.
 */
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');
}

/**
 * Prepare a palette for rendering: apply hue/sat/brightness adjustments.
 * Returns { bg, bgHex, bgRgb, colors, colorsHex, rgbColors, border, borderHex, stroke, strokeHex }
 *
 * The 'Hex' variants are needed by geo renderers that do things like `pal.bg + '60'`
 * for hex-alpha transparency. The non-Hex variants are rgb() strings.
 */
export function preparePalette(palette, hueShift = 0, satMul = 1, briMul = 1) {
  const adjustRgb = (hex) => {
    const [r, g, b] = hexToRgb(hex);
    let [h, s, l] = rgbToHsl(r, g, b);
    h += hueShift / 360;
    s = Math.min(1, Math.max(0, s * satMul));
    l = Math.min(1, Math.max(0, l * briMul));
    return hslToRgb(h, s, l);
  };

  const bgRgb = adjustRgb(palette.bg);
  const colorsRgb = palette.colors.map(c => adjustRgb(c));
  const borderRgb = palette.border ? adjustRgb(palette.border) : undefined;
  const strokeRgb = palette.stroke ? adjustRgb(palette.stroke) : undefined;

  return {
    bg: `rgb(${bgRgb[0]},${bgRgb[1]},${bgRgb[2]})`,
    bgHex: rgbToHex(...bgRgb),
    bgRgb,
    colors: colorsRgb.map(([r, g, b]) => `rgb(${r},${g},${b})`),
    colorsHex: colorsRgb.map(([r, g, b]) => rgbToHex(r, g, b)),
    rgbColors: colorsRgb,
    // Geo-specific fields (both rgb string and hex)
    border: borderRgb ? `rgb(${borderRgb[0]},${borderRgb[1]},${borderRgb[2]})` : undefined,
    borderHex: borderRgb ? rgbToHex(...borderRgb) : undefined,
    stroke: strokeRgb ? `rgb(${strokeRgb[0]},${strokeRgb[1]},${strokeRgb[2]})` : undefined,
    strokeHex: strokeRgb ? rgbToHex(...strokeRgb) : undefined,
  };
}

// ═══════════════════════════════════════════════════════════
//  DEFAULT CONFIG
// ═══════════════════════════════════════════════════════════

export const DEFAULT_GENART_CONFIG = {
  enabled: false,
  family: 'organic',
  type: 'ridged',
  paletteId: 'organic-ocean',
  seed: 42,
  hueShift: 0,
  saturation: 100,
  brightness: 100,
  darken: 0,
  params: { scale: 0.50, density: 0.50, complexity: 5.0 },
};

// ═══════════════════════════════════════════════════════════
//  BACKWARD COMPATIBILITY — old SVG preset → new type mapping
// ═══════════════════════════════════════════════════════════

export const LEGACY_TYPE_MAP = {
  nebula:  { family: 'organic', type: 'domainWarp',  paletteId: 'organic-midnight' },
  smoke:   { family: 'organic', type: 'smoke',       paletteId: 'organic-arctic'   },
  silk:    { family: 'flow',    type: 'silkThreads',  paletteId: 'organic-ocean'   },
  terrain: { family: 'organic', type: 'topography',   paletteId: 'organic-forest'  },
  pulse:   { family: 'organic', type: 'particles',    paletteId: 'organic-midnight' },
  liquid:  { family: 'flow',    type: 'fluid',        paletteId: 'organic-ocean'   },
  waves:   { family: 'organic', type: 'topography',   paletteId: 'organic-ocean'   },
  ridge:   { family: 'flow',    type: 'ridged',       paletteId: 'organic-ocean'   },
};

// ═══════════════════════════════════════════════════════════
//  CLIENT-SIDE MIGRATION — old SVG → new Canvas2D config
// ═══════════════════════════════════════════════════════════

/**
 * Migrate a generative config from old SVG format to new Canvas2D format.
 * Returns the config unchanged if already in new format or null/disabled.
 * This is idempotent — safe to call multiple times.
 *
 * @param {Object|null} config — slide.generative object
 * @returns {Object|null} — migrated config (or original if no migration needed)
 */
export function migrateGenerativeConfig(config) {
  if (!config) return config;

  // Already new format — has `type` that matches a known generator
  if (config.type && GENERATOR_MAP[config.type]) return config;

  // Old SVG format — has `preset` field
  if (config.preset) {
    const mapping = LEGACY_TYPE_MAP[config.preset];
    if (mapping) {
      return {
        enabled: config.enabled ?? false,
        family: mapping.family,
        type: mapping.type,
        paletteId: mapping.paletteId,
        seed: config.seed ?? 42,
        hueShift: 0,
        saturation: 100,
        brightness: 100,
        darken: config.darken ?? 0,
        params: getDefaultParams(mapping.type),
      };
    }
  }

  // Unknown format — return as-is (normalization in editor/canvas handles fallback)
  return config;
}
